import { useState, useEffect } from 'react';
import axios from 'axios';
import useLogger from 'hooks/useLogger';
import { LoggerAdditionalType, LoggerMessageType, ProductStyle } from 'types';

const IMAGE_HOST = 'https://dks.scene7.com/is/image/dkscdn';

type Scene7ImageItem = {
  i?: {
    n: string;
  };
};

type Scene7Response = {
  set?: {
    item: Scene7ImageItem[] | Scene7ImageItem;
  };
};

const useScene7MediaSet = (
  productStyle: ProductStyle,
  defaultColor = '',
): [string[], boolean, string | null, (color: string) => void] => {
  const [productMedia, setProductMedia] = useState<string[]>([]);
  const [selectedColor, setSelectedColor] = useState<string | null>(defaultColor || null);
  const [loading, setLoading] = useState<boolean>(true);
  const { warn } = useLogger();

  const formatProductMedia = (mediaSetResponse: Scene7ImageItem[] | Scene7ImageItem): string[] => {
    try {
      if (Array.isArray(mediaSetResponse)) {
        const productImagesOnly = mediaSetResponse.filter((media) => media.i && media.i.n);
        return productImagesOnly
          .map((image) =>
            image.i?.n
              ? `https://dks.scene7.com/is/image/${image.i.n}/?wid=500&qlt=85,0&fmt=jpg&op_sharpen=1`
              : null,
          )
          .filter(Boolean) as string[];
      }

      if (mediaSetResponse.i?.n) {
        return [
          `https://dks.scene7.com/is/image/${mediaSetResponse.i.n}/?wid=500&qlt=85,0&fmt=jpg&op_sharpen=1`,
        ];
      }

      return [];
    } catch (e) {
      warn('useScene7MediaSet - format error', e as LoggerAdditionalType); // Check if this is the correct type because forcing the type is not a good practice unless we are sure of the type
      return [];
    }
  };

  const cleanResponse = (responseData: string): Scene7Response | null => {
    try {
      const cleanedResponseData = responseData
        .replace('/*jsonp*/', '')
        .replace('customScene7Handler(', '')
        .replace(', "");', '')
        .replace(',"");', '')
        .trim();

      return JSON.parse(cleanedResponseData);
    } catch (error) {
      warn(error as LoggerMessageType); // Check if this is the correct type because forcing the type is not a good practice unless we are sure of the type
      return null;
    }
  };

  const generateScene7Url = (): string => {
    const colorAttrIndex =
      productStyle.definingAttributes?.findIndex((element) => element.name === 'Color') ?? -1;

    let color = '';
    if (selectedColor) {
      color = selectedColor.replace(/[^\w]/gi, '_').replace(/(_+)\1+/g, '$1');
    } else if (colorAttrIndex !== -1 && productStyle.definingAttributes !== undefined) {
      const tmpDefinigAttribute = productStyle.definingAttributes[colorAttrIndex];
      const firstColor = tmpDefinigAttribute.value.split('|')[0];
      color = firstColor.replace(/[^\w]/gi, '_').replace(/(_+)\1+/g, '$1');
    }

    return `${IMAGE_HOST}/${productStyle.partNumber}${
      color ? `_${color}` : ''
    }_is?req=set,json,UTF-8&labelkey=label&handler=customScene7Handler`;
  };

  useEffect(() => {
    const fetchMediaSet = async () => {
      try {
        setLoading(true);
        const scene7Url = generateScene7Url();
        const response = await axios.get(scene7Url);

        const parsed = cleanResponse(response.data);
        if (parsed?.set?.item) {
          const images = formatProductMedia(parsed.set.item);
          setProductMedia(images);
        } else {
          throw new Error('Empty image set or items');
        }
      } catch (error) {
        warn(error as LoggerMessageType); // Check if this is the correct type because forcing the type is not a good practice unless we are sure of the type
      } finally {
        setLoading(false);
      }
    };

    fetchMediaSet();
  }, [selectedColor]);

  return [productMedia, loading, selectedColor, setSelectedColor];
};

export default useScene7MediaSet;
